import { Die } from '../types/game';
import { MaxiYatzyScores, MaxiScoreKey } from '../types/maxi';

export interface MaxiScoreCategory {
  key: MaxiScoreKey;
  name: string;
  description: string;
  isUpperSection: boolean;
}

export const maxiScoreCategories: MaxiScoreCategory[] = [
  { key: 'ones', name: 'Ettor', description: 'Summa av alla ettor', isUpperSection: true },
  { key: 'twos', name: 'Tvåor', description: 'Summa av alla tvåor', isUpperSection: true },
  { key: 'threes', name: 'Treor', description: 'Summa av alla treor', isUpperSection: true },
  { key: 'fours', name: 'Fyror', description: 'Summa av alla fyror', isUpperSection: true },
  { key: 'fives', name: 'Femmor', description: 'Summa av alla femmor', isUpperSection: true },
  { key: 'sixes', name: 'Sexor', description: 'Summa av alla sexor', isUpperSection: true },
  { key: 'onePair', name: 'Ett par', description: 'Högsta par × 2', isUpperSection: false },
  { key: 'twoPairs', name: 'Två par', description: 'Två par, båda räknas', isUpperSection: false },
  { key: 'threeOfAKind', name: 'Triss', description: 'Tre lika × 3', isUpperSection: false },
  { key: 'fourOfAKind', name: 'Fyrtal', description: 'Fyra lika × 4', isUpperSection: false },
  { key: 'fiveOfAKind', name: 'Femtal', description: 'Fem lika × 5', isUpperSection: false },
  { key: 'smallStraight', name: 'Liten stege', description: '1-2-3-4-5 = 15 poäng', isUpperSection: false },
  { key: 'largeStraight', name: 'Stor stege', description: '2-3-4-5-6 = 20 poäng', isUpperSection: false },
  { key: 'fullStraight', name: 'Full stege', description: '1-2-3-4-5-6 = 21 poäng', isUpperSection: false },
  { key: 'hus', name: 'Hus', description: 'Två triss (3 + 3)', isUpperSection: false },
  { key: 'torn', name: 'Torn', description: 'Fyra + par (4 + 2)', isUpperSection: false },
  { key: 'chance', name: 'Chans', description: 'Summa av alla tärningar', isUpperSection: false },
  { key: 'yatzy', name: 'Yatzy', description: 'Sex lika = 100 poäng', isUpperSection: false },
];

const countValues = (dice: Die[]): number[] => {
  const counts = new Array(7).fill(0);
  dice.forEach(die => counts[die.value]++);
  return counts;
};

const getDiceValues = (dice: Die[]): number[] => dice.map(d => d.value).sort((a, b) => a - b);

export const calculateMaxiScore = (dice: Die[], category: MaxiScoreKey): number => {
  const values = getDiceValues(dice);
  const counts = countValues(dice);

  switch (category) {
    case 'ones':
    case 'twos':
    case 'threes':
    case 'fours':
    case 'fives':
    case 'sixes': {
      const idx = ['ones','twos','threes','fours','fives','sixes'].indexOf(category) + 1;
      return counts[idx] * idx;
    }
    case 'onePair': {
      for (let i = 6; i >= 1; i--) if (counts[i] >= 2) return i * 2;
      return 0;
    }
    case 'twoPairs': {
      const pairs: number[] = [];
      for (let i = 6; i >= 1; i--) if (counts[i] >= 2) pairs.push(i);
      return pairs.length >= 2 ? pairs[0]*2 + pairs[1]*2 : 0;
    }
    case 'threeOfAKind': {
      for (let i = 6; i >= 1; i--) if (counts[i] >= 3) return i*3;
      return 0;
    }
    case 'fourOfAKind': {
      for (let i = 6; i >= 1; i--) if (counts[i] >= 4) return i*4;
      return 0;
    }
    case 'fiveOfAKind': {
      for (let i = 6; i >= 1; i--) if (counts[i] >= 5) return i*5;
      return 0;
    }
    case 'smallStraight': {
      const need = [1,2,3,4,5];
      return need.every(v => counts[v] >= 1) ? 15 : 0;
    }
    case 'largeStraight': {
      const need = [2,3,4,5,6];
      return need.every(v => counts[v] >= 1) ? 20 : 0;
    }
    case 'fullStraight': {
      const need = [1,2,3,4,5,6];
      return need.every(v => counts[v] >= 1) ? 21 : 0;
    }
    case 'hus': {
      // Två triss: två olika värden med count >=3
      const trips: number[] = [];
      for (let i = 6; i >= 1; i--) if (counts[i] >= 3) trips.push(i);
      if (trips.length < 2) return 0; // måste vara minst två olika triss
      // Ta de två högsta trisserna
      return trips[0]*3 + trips[1]*3;
    }
    case 'torn': {
      // Fyra + par: ett värde count>=4 och ett annat värde count>=2
      let fourVal: number | null = null;
      let pairVal: number | null = null;
      for (let i = 6; i >= 1; i--) if (counts[i] >= 4) { fourVal = i; break; }
      if (!fourVal) return 0;
      for (let i = 6; i >= 1; i--) if (i !== fourVal && counts[i] >= 2) { pairVal = i; break; }
      if (!pairVal) return 0;
      // Poäng = summan av alla 6 tärningar i kombinationen (fyraVal *4 + pairVal*2)
      return fourVal*4 + pairVal*2;
    }
    case 'chance':
      return values.reduce((s, v) => s + v, 0);
    case 'yatzy': {
      for (let i = 1; i <= 6; i++) {
        if (counts[i] === 6) return 100; // Endast exakt sex lika
      }
      return 0;
    }
  }
};

export const calculateMaxiTotals = (scores: MaxiYatzyScores) => {
  const upperKeys: MaxiScoreKey[] = ['ones','twos','threes','fours','fives','sixes'];
  const upper = upperKeys.reduce((s, k) => s + (scores[k] || 0), 0);
  // Enligt dina regler: 84+ ger 50 bonus
  const bonus = upper >= 84 ? 50 : 0;
  const lower = (Object.keys(scores) as MaxiScoreKey[])
    .filter(k => !upperKeys.includes(k))
    .reduce((s, k) => s + (scores[k] || 0), 0);
  return { total: upper + bonus + lower, upper, bonus };
};
